import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, FD_TYPES, tFdType } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import { CritBadge, StatusBadge, EmptyState } from "@/components/shared/Badges";
import FailoverPolicyDialog from "@/components/shared/FailoverPolicyDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Layers, Plus, Pencil, Trash2, Server, ArrowRight, SlidersHorizontal, ShieldAlert } from "lucide-react";

const EMPTY = { name: "", type: "DATACENTER", description: "", criticality: "medium", status: "active" };

function DomainDetail({ id, onClose, onNavigate }) {
  const { data } = useQuery({
    queryKey: ["failure-domain", id],
    queryFn: async () => (await api.get(`/failure-domains/${id}`)).data,
    enabled: !!id,
  });
  if (!data) return null;
  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="fd-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Layers className="h-4 w-4 text-primary" /> {data.name}</DialogTitle>
          <DialogDescription>{data.type_label} — {data.description || "Aucune description."}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 py-1">
          <CritBadge value={data.criticality} /><StatusBadge value={data.status} />
        </div>
        <div>
          <h4 className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2">Actifs appartenant à ce domaine — {data.assets?.length || 0}</h4>
          {data.assets?.length ? (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto cros-scroll">
              {data.assets.map((a) => (
                <li key={a.id}>
                  <button className="flex items-center gap-2 text-sm hover:text-primary w-full" onClick={() => onNavigate(a.id)} data-testid={`fd-asset-${a.id}`}>
                    <Server className="h-3.5 w-3.5 text-muted-foreground" /> {a.name}
                    <span className="ml-auto"><CritBadge value={a.criticality} /></span>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-muted-foreground">Aucun actif associé. L'association se fait depuis la fiche d'un actif.</p>}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FailureDomains() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [detailId, setDetailId] = useState(routeId || null);
  const [policyOpen, setPolicyOpen] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["failure-domains"],
    queryFn: async () => (await api.get("/failure-domains")).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["failure-domains"] });
  const save = useMutation({
    mutationFn: async () => editing
      ? (await api.put(`/failure-domains/${editing}`, form)).data
      : (await api.post("/failure-domains", form)).data,
    onSuccess: () => { toast.success(editing ? "Domaine mis à jour" : "Domaine créé"); setOpen(false); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/failure-domains/${id}`),
    onSuccess: () => { toast.success("Domaine supprimé (les actifs sont conservés)"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (d) => { setEditing(d.id); setForm({ name: d.name, type: d.type, description: d.description || "", criticality: d.criticality, status: d.status }); setOpen(true); };
  const writable = can("asset:write");

  return (
    <AppShell title="Domaines de défaillance" subtitle="FAILURE_DOMAIN — périmètres communs pouvant affecter plusieurs actifs simultanément"
      actions={
        <div className="flex items-center gap-2">
          {can("dependency:write") && <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => setPolicyOpen(true)} data-testid="open-policy-button"><SlidersHorizontal className="h-4 w-4" /> Politique de bascule</Button>}
          {writable && <Button size="sm" className="rounded-sm gap-2" onClick={openCreate} data-testid="create-fd-button"><Plus className="h-4 w-4" /> Nouveau domaine</Button>}
        </div>
      }>
      {isLoading ? (
        <p className="text-sm text-muted-foreground font-mono">Chargement…</p>
      ) : rows.length === 0 ? (
        <EmptyState icon={Layers} title="Aucun domaine de défaillance"
          hint="Créez des domaines (Datacenter, Stockage, Hyperviseur…) puis associez-y vos actifs depuis leur fiche." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fd-grid">
          {rows.map((d) => (
            <div key={d.id} className="border border-border bg-card rounded-sm p-5 flex flex-col" data-testid={`fd-card-${d.id}`}>
              <div className="flex items-start justify-between">
                <button className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center mb-3 hover:bg-primary/20" onClick={() => setDetailId(d.id)}>
                  <Layers className="h-5 w-5 text-primary" />
                </button>
                {writable && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(d)} data-testid={`edit-fd-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => { if (window.confirm("Supprimer ce domaine ? Les actifs ne sont pas supprimés.")) del.mutate(d.id); }} data-testid={`delete-fd-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
              <button className="text-left" onClick={() => setDetailId(d.id)} data-testid={`fd-open-${d.id}`}>
                <h3 className="font-head font-semibold">{d.name}</h3>
              </button>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="rounded-sm font-mono text-[10px]">{d.type_label}</Badge>
                <CritBadge value={d.criticality} />
              </div>
              {d.description && <p className="mt-3 text-xs text-muted-foreground leading-relaxed line-clamp-3">{d.description}</p>}
              <div className="mt-auto pt-3 text-xs text-muted-foreground flex items-center gap-1.5">
                <Server className="h-3.5 w-3.5" /> {d.asset_count} actif(s) associé(s)
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-start gap-2 text-[11px] text-muted-foreground/80 max-w-3xl">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
        <p>L'absence de domaine de défaillance commun connu dans CROS ne prouve pas l'absence d'une dépendance commune réelle dans l'infrastructure. Les qualifications restent « connues » / « potentielles ».</p>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border rounded-sm" data-testid="fd-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">{editing ? "Modifier le domaine" : "Nouveau domaine de défaillance"}</DialogTitle>
            <DialogDescription>Un domaine peut contenir plusieurs actifs ; un actif peut appartenir à plusieurs domaines.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="fd-name-input" placeholder="Ex : DC-PARIS-01, SAN-01…" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="fd-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{FD_TYPES.map((t) => <SelectItem key={t} value={t}>{tFdType(t)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Criticité</Label>
                <Select value={form.criticality} onValueChange={(v) => setForm({ ...form, criticality: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critique</SelectItem>
                    <SelectItem value="high">Élevée</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="low">Faible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="inactive">Inactif</SelectItem>
                    <SelectItem value="planned">Planifié</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="rounded-sm" onClick={() => save.mutate()} disabled={!form.name || save.isPending} data-testid="save-fd-button">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DomainDetail id={detailId} onClose={() => setDetailId(null)} onNavigate={(aid) => { setDetailId(null); navigate(`/assets/${aid}`); }} />
      <FailoverPolicyDialog open={policyOpen} onOpenChange={setPolicyOpen} />
    </AppShell>
  );
}
